import {DeliveryOption, getSavingsPercent} from '@/product/delivery-option';

import {BadgeSettings} from '@/settings/types';
import Settings from '@/components/buy-stack/ProductSelectSettings';
import classNames from 'classnames';
import createPackSizeBadge from '@/settings/create-pack-size-badge';
import {getColorStyle} from '@/utilities/color-style-map';
import {useState} from 'preact/hooks';

/**
 * A selector for choosing between pack sizes.
 * This is meant to be used in the DeliverySelector component.
 */
export default function PackSizeSelector({
  color = 'primary-blue',
  onUpdate,
  options,
  value,
}: PackSizeSelectorProps) {
  const [selected, setSelected] = useState<DeliveryOption | null>(
    value ?? options[0] ?? null
  );

  const handleSelectOption = (option: DeliveryOption) => {
    setSelected(option);
    onUpdate && onUpdate(option);
  };

  const columns = options.length > 3 ? 'grid-cols-4' : 'grid-cols-3';
  const colorStyle = getColorStyle(color);

  return (
    <Settings.Consumer>
      {(settings) => (
        <div className={classNames('grid gap-2.5 pt-2', columns)}>
          {options.map((option) => {
            // TODO: Find a more flexible solution than just using the first item for pack size and price
            // It is likely this will not work for all cases
            const {price, packSize} = option.items[0];
            const isSelected = packSize === selected?.items[0]?.packSize;
            const savingsPercent = getSavingsPercent(option);
            const packs = packSize;
            const badge = createPackSizeBadge(packs, settings);

            return (
              <button
                key={price}
                data-testid={`pack-size-${packSize}`}
                className={classNames(
                  'fast-transition relative flex h-max flex-col items-center justify-center rounded-md border-2 py-3 leading-normal',
                  {
                    [`${colorStyle.border} ${colorStyle.background} text-bright-white`]:
                      isSelected,
                    'bg-bright-white text-neutral-dark-gray': !isSelected,
                    [badge?.color.border ?? '']: !isSelected && badge,
                  }
                )}
                onClick={() => handleSelectOption(option)}
              >
                {badge && (
                  <span
                    className={classNames(
                      'absolute left-1/2 top-0 z-10 w-max -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded px-1 py-0.5 text-[7px] font-semibold uppercase tracking-[0.5px] text-bright-white',
                      badge.color.background
                    )}
                  >
                    {badge.label}
                  </span>
                )}{' '}
                <span
                  className={classNames('text-2xs font-medium', {
                    [badge?.color.text ?? 'text-primary-blue']: !isSelected,
                  })}
                >
                  SAVE {savingsPercent}%
                </span>
                <h5>{packSize}</h5>
                <span className="text-2xs font-medium">PACK</span>
              </button>
            );
          })}
        </div>
      )}
    </Settings.Consumer>
  );
}

export type PackSizeSelectorProps = {
  color?: string;
  onUpdate?: (option: DeliveryOption) => void;
  options: DeliveryOption[];
  value?: DeliveryOption;
};

export type PackSizeSelectorSettings = BadgeSettings;
